/* VIZZYO BF2026 — WOW r3 motion layer (2026-10-24).
   Loaded only while the campaign runs (snippets/vzy-bf-config.liquid, deferred). No dependencies, ~4 KB gz.
   Everything here is decoration on top of markup that is already complete and correct without JS:
   1. Reveal once on view: BF badges/strips/cards, PDP ladder + flag, BF2026 code chips on product cards and the
      PDP price (.is-seen / .is-shone; the PDP chip is re-rendered on quantity change, so it shines again).
   2. PDP: voucher amounts count to their new value, the reached tier chip pops, the newly selected ticket shines.
      Done by observing vizzyo-quantity-breaks, so assets/vzy-quantity-breaks.js is not touched.
   3. Cart drawer: the BF panel's bar grows from the previous value, the saved lei count up, and a short
      celebration (≤ 600 ms) runs when the tier goes up. Effects wait until the drawer is open and the
      re-renders (theme + vzy-bf-code.js) have settled, so they are never cut in half.
   4. Countdown: [data-vzy-bf-countdown] shows the calm end date; only in the last 72 h it ticks
      ("Se termină în 2 zile 14:32:10"), only while visible and the tab is visible. Screen readers get the
      static end date (the ticking text is aria-hidden in the markup).
   All motion respects prefers-reduced-motion (CSS + checks here); values are never invented — every number
   shown at the end of an animation is the exact text the server or the theme rendered. */
(() => {
  if (window.__vzyBfMotion) return;
  window.__vzyBfMotion = true;

  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canAnimate = () => !reduce.matches;
  const hasIO = 'IntersectionObserver' in window;
  const raf = (fn) => window.requestAnimationFrame(fn);

  const inView = (el) => {
    if (!el || !el.isConnected) return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
  };

  const restartClass = (el, className, duration) => {
    if (!el) return;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
    window.setTimeout(() => el.classList.remove(className), duration);
  };

  /* ---------------------------------------------------------------- money count-up */

  // "1.222,65 lei", "−34,32 lei", "84,37 lei" → { prefix, cents, suffix }
  const parseLei = (text) => {
    const match = String(text || '').match(/^([^\d]*?)(\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?|\d+(?:,\d{1,2})?)([\s\S]*)$/);
    if (!match) return null;
    const cents = Math.round(Number(match[2].replace(/\./g, '').replace(',', '.')) * 100);
    return Number.isFinite(cents) ? { prefix: match[1], cents, suffix: match[3], decimals: match[2].includes(',') } : null;
  };
  let numberFormat = null;
  const formatCents = (cents, decimals) => {
    const value = cents / 100;
    try {
      numberFormat = numberFormat || new Intl.NumberFormat('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return decimals ? numberFormat.format(value) : Math.round(value).toLocaleString('ro-RO');
    } catch (error) {
      return value.toFixed(decimals ? 2 : 0).replace('.', ',');
    }
  };

  const written = new WeakMap(); // element → last text this script wrote (to ignore our own mutations)
  const counting = new WeakMap(); // element → { frame, shown }

  // Shows `fromCents` now (before paint), then counts to the exact `finalText` after `delay` ms.
  const countTo = (el, fromCents, finalText, { duration = 520, delay = 0 } = {}) => {
    const target = parseLei(finalText);
    const previous = counting.get(el);
    if (previous) window.cancelAnimationFrame(previous.frame);
    const liveRegion = el.closest('[aria-live]');
    if (!target || fromCents == null || fromCents === target.cents || !canAnimate() || document.visibilityState !== 'visible') {
      counting.delete(el);
      liveRegion?.removeAttribute('aria-busy');
      if (el.textContent !== finalText) {
        written.set(el, finalText);
        el.textContent = finalText;
      }
      return;
    }
    const state = { frame: 0, shown: fromCents };
    counting.set(el, state);
    // Screen readers: announce only the final amount, not the intermediate frames.
    liveRegion?.setAttribute('aria-busy', 'true');
    const paint = (cents, text) => {
      state.shown = cents;
      written.set(el, text);
      el.textContent = text;
    };
    paint(fromCents, `${target.prefix}${formatCents(fromCents, target.decimals)}${target.suffix}`);
    const begin = () => {
      let start = null;
      const step = (now) => {
        if (!el.isConnected) return;
        // The first frame's timestamp is the start (a rAF time can be earlier than performance.now()).
        if (start === null) start = now;
        const progress = Math.min(1, Math.max(0, (now - start) / duration));
        const eased = 1 - Math.pow(1 - progress, 3);
        if (progress < 1) {
          const cents = Math.round(fromCents + (target.cents - fromCents) * eased);
          paint(cents, `${target.prefix}${formatCents(cents, target.decimals)}${target.suffix}`);
          state.frame = raf(step);
        } else {
          paint(target.cents, finalText);
          counting.delete(el);
          liveRegion?.removeAttribute('aria-busy');
        }
      };
      state.frame = raf(step);
    };
    if (delay > 0) window.setTimeout(begin, delay);
    else begin();
    window.setTimeout(() => {
      if (counting.get(el) !== state) return;
      window.cancelAnimationFrame(state.frame);
      counting.delete(el);
      if (el.isConnected) paint(target.cents, finalText);
      liveRegion?.removeAttribute('aria-busy');
    }, delay + duration + 250);
  };

  /* ---------------------------------------------------------------- 1. reveal once on view */

  const REVEAL = '.vzy-bftier--strip, .vzy-bftier--card, .vzy-qb__bf-ladder, .vzy-qb__eyebrow--bf, .vzy-card__bf-code, .vzy-smart-price-total__bf-code';
  const CHIP = /(vzy-card__bf-code|vzy-smart-price-total__bf-code)/;
  const revealObserver = hasIO ? new IntersectionObserver((entries) => {
    let batch = 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      revealObserver.unobserve(el);
      if (CHIP.test(el.className)) {
        // Code chips that scroll in together shine one after another (max 6 steps), then become hover-only.
        const delay = 200 + Math.min(batch, 5) * 90;
        batch += 1;
        el.style.setProperty('--vzy-bf-delay', `${delay}ms`);
        window.setTimeout(() => el.classList.add('is-shone'), delay + 1000);
      }
      el.classList.add('is-seen');
    });
  }, { threshold: 0.35 }) : null;

  const watchReveal = (scope) => {
    const nodes = scope.matches?.(REVEAL) ? [scope] : Array.from(scope.querySelectorAll?.(REVEAL) || []);
    nodes.forEach((el) => {
      if (el.dataset.vzyBfReveal) return;
      el.dataset.vzyBfReveal = '1';
      if (revealObserver) revealObserver.observe(el);
      else el.classList.add('is-seen', 'is-shone');
    });
  };

  /* ---------------------------------------------------------------- 2. PDP package rows */

  const currentIndex = (list) => Array.from(list?.children || []).findIndex((item) => item.classList.contains('is-current'));

  const setupQuantityBreaks = (qb) => {
    if (qb.dataset.vzyBfMotion) return;
    qb.dataset.vzyBfMotion = '1';
    const savings = new WeakMap();
    qb.querySelectorAll('[data-vzy-qb-saving]').forEach((el) => {
      const parsed = parseLei(el.textContent);
      if (parsed) savings.set(el, parsed.cents);
    });
    const nextAmount = () => qb.querySelector('.vzy-qb__bf-next-sub b');
    let nextCents = parseLei(nextAmount()?.textContent)?.cents ?? null;
    const steps = qb.querySelector('.vzy-qb__bf-steps');
    let stepIndex = currentIndex(steps);

    new MutationObserver((records) => {
      const amounts = new Set();
      let footer = false;
      let stepsChanged = false;
      const selected = new Set();
      records.forEach((record) => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (!target) return;
        if (record.type === 'attributes') {
          if (record.attributeName === 'data-selected' && target.hasAttribute('data-selected')) selected.add(target);
          else if (record.attributeName === 'class' && target.parentElement === steps) stepsChanged = true;
          return;
        }
        const saving = target.closest('[data-vzy-qb-saving]');
        if (saving) amounts.add(saving);
        if (target.closest('[data-vzy-qb-voucher-text]')) footer = true;
      });

      amounts.forEach((el) => {
        if (written.get(el) === el.textContent) return;
        const parsed = parseLei(el.textContent);
        if (!parsed) return;
        const from = counting.get(el)?.shown ?? savings.get(el);
        savings.set(el, parsed.cents);
        if (inView(el)) countTo(el, from, el.textContent);
      });

      if (footer) {
        const el = nextAmount();
        if (el && written.get(el) !== el.textContent) {
          const parsed = parseLei(el.textContent);
          if (parsed) {
            const from = nextCents;
            nextCents = parsed.cents;
            if (inView(el)) countTo(el, from, el.textContent);
          }
        }
      }

      if (stepsChanged) {
        const index = currentIndex(steps);
        if (index > stepIndex && stepIndex > -1 && canAnimate() && inView(steps)) {
          restartClass(steps.children[index], 'vzy-bf-pop', 600);
        }
        stepIndex = index;
      }

      if (canAnimate()) {
        selected.forEach((option) => {
          const ticket = option.querySelector('.vzy-qb__saving--bf');
          if (ticket && inView(ticket)) restartClass(ticket, 'vzy-bf-shine', 1300);
        });
      }
    }).observe(qb, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'data-selected'] });
  };

  /* ---------------------------------------------------------------- 3. cart drawer BF panel */

  const drawer = document.querySelector('cart-drawer');
  const panelSelector = '.vzy-drawer-aov--bf[data-vzy-bf-cameras]';
  const drawerOpen = () => Boolean(drawer?.classList.contains('active'));

  const snapshot = (panel) => {
    if (!panel) return { tier: -1, progress: 0, savings: null };
    let tier = Number(panel.dataset.vzyBfTier);
    if (!Number.isFinite(tier)) tier = currentIndex(panel.querySelector('.vzy-drawer-aov__tiers--bf'));
    const amount = panel.querySelector('[data-vzy-bf-amount]');
    return {
      tier,
      progress: parseFloat(panel.style.getPropertyValue('--vzy-aov-progress')) || 0,
      savings: amount ? parseLei(amount.textContent)?.cents ?? null : null,
      other: panel.classList.contains('vzy-drawer-aov--other')
    };
  };

  // What the shopper last saw; effects always start from here.
  let seen = null;
  let lastPanel = null;
  let settleTimer = 0;
  let settleSince = 0;
  let entranceDone = false;

  // Show the previous bar width / amount on a freshly rendered panel (before paint), so nothing jumps.
  const rewind = (panel) => {
    if (!seen || !canAnimate()) return;
    const bar = panel.querySelector('.vzy-drawer-aov__progress span');
    const now = snapshot(panel);
    if (bar && now.progress !== seen.progress) {
      bar.style.transition = 'none';
      bar.style.width = `${seen.progress}%`;
    }
    const amount = panel.querySelector('[data-vzy-bf-amount]');
    if (amount && now.savings != null && seen.savings !== now.savings) {
      const from = seen.savings ?? 0;
      const parsed = parseLei(amount.textContent);
      if (parsed) {
        amount.dataset.vzyBfFinal = amount.textContent;
        amount.closest('[aria-live]')?.setAttribute('aria-busy', 'true');
        const text = `${parsed.prefix}${formatCents(from, parsed.decimals)}${parsed.suffix}`;
        written.set(amount, text);
        amount.textContent = text;
      }
    }
  };

  const release = (panel, { animate }) => {
    const bar = panel.querySelector('.vzy-drawer-aov__progress span');
    if (bar && bar.style.width) {
      void bar.offsetWidth;
      bar.style.transition = animate ? '' : 'none';
      bar.style.width = '';
      if (!animate) raf(() => { bar.style.transition = ''; });
    }
    const amount = panel.querySelector('[data-vzy-bf-amount][data-vzy-bf-final]');
    if (amount) {
      const finalText = amount.dataset.vzyBfFinal;
      delete amount.dataset.vzyBfFinal;
      const from = parseLei(amount.textContent)?.cents;
      if (animate) countTo(amount, from, finalText, { duration: 600 });
      else {
        written.set(amount, finalText);
        amount.textContent = finalText;
        amount.closest('[aria-live]')?.removeAttribute('aria-busy');
      }
    }
  };

  const settle = () => {
    settleTimer = 0;
    const panel = drawer?.querySelector(panelSelector) || null;
    const now = snapshot(panel);
    if (panel && !drawerOpen() && Date.now() - settleSince < 2500) {
      // Rendered while closed (add to cart opens it a moment later): wait for the drawer to open.
      settleTimer = window.setTimeout(settle, 120);
      return;
    }
    settleSince = 0;
    if (panel) {
      const animate = canAnimate() && drawerOpen();
      const celebrate = animate && seen && !now.other && now.tier > seen.tier;
      window.setTimeout(() => {
        if (!panel.isConnected) return;
        release(panel, { animate });
        if (celebrate) restartClass(panel, 'vzy-bf-celebrate', 900);
      }, animate ? 220 : 0);
      entranceDone = entranceDone || drawerOpen();
    }
    seen = { ...now, savings: now.savings ?? seen?.savings ?? null };
    if (!panel) seen.savings = null;
  };

  const onDrawerChange = () => {
    const panel = drawer.querySelector(panelSelector);
    if (panel === lastPanel) return;
    lastPanel = panel;
    if (seen === null) {
      seen = snapshot(panel); // first look at page load: no effects
      return;
    }
    if (panel) rewind(panel);
    if (!settleSince) settleSince = Date.now();
    window.clearTimeout(settleTimer);
    settleTimer = window.setTimeout(settle, 380);
  };

  const setupDrawer = () => {
    if (!drawer || !('MutationObserver' in window)) return;
    onDrawerChange();
    new MutationObserver(onDrawerChange).observe(drawer, { childList: true, subtree: true });
    // First open in this page view: the bar grows from 0 once.
    new MutationObserver(() => {
      if (entranceDone || !drawerOpen() || settleTimer || !canAnimate()) return;
      const panel = drawer.querySelector(panelSelector);
      entranceDone = true;
      if (!panel || panel.classList.contains('vzy-drawer-aov--other')) return;
      const bar = panel.querySelector('.vzy-drawer-aov__progress span');
      if (!bar) return;
      bar.style.transition = 'none';
      bar.style.width = '0%';
      window.setTimeout(() => release(panel, { animate: true }), 260);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  };

  /* ---------------------------------------------------------------- 4. countdown */

  const countdowns = new Set();
  const visibleCountdowns = new Set();
  let tickTimer = 0;
  const URGENT_MS = 72 * 3600 * 1000;
  // Same device-clock grace as vzy-bf-code.js / vzy-quantity-breaks.js: the server decides the real end.
  const CLOCK_GRACE = 15 * 60 * 1000;
  const pad = (value) => String(value).padStart(2, '0');

  const paintCountdown = (el, now) => {
    const end = Date.parse(el.dataset.end || '');
    const text = el.querySelector('[data-vzy-bf-countdown-text]');
    if (!Number.isFinite(end) || !text) return false;
    const left = end - now;
    if (left <= 0 && left > -CLOCK_GRACE) {
      if (text.textContent !== 'Oferta se încheie acum') text.textContent = 'Oferta se încheie acum';
      el.classList.add('is-urgent');
      return true;
    }
    if (left <= 0) {
      text.textContent = 'Oferta s-a încheiat';
      el.classList.remove('is-urgent');
      // Same rule as vzy-bf-code.js for cached pages: no BF claims after the end.
      root.classList.add('vzy-bf-ended');
      return false;
    }
    if (left > URGENT_MS) {
      if (el.dataset.calm && text.textContent !== el.dataset.calm) text.textContent = el.dataset.calm;
      el.classList.remove('is-urgent');
      return false;
    }
    el.classList.add('is-urgent');
    const seconds = Math.floor(left / 1000);
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const dayLabel = days === 0 ? '' : days === 1 ? '1 zi ' : `${days} zile `;
    // Reduced motion: minutes only (no ticking seconds).
    const clock = reduce.matches
      ? `${hours} h ${pad(minutes)} min`
      : `${pad(hours)}:${pad(minutes)}:${pad(seconds % 60)}`;
    const value = `Se termină în ${dayLabel}${clock}`;
    if (text.textContent !== value) text.textContent = value;
    return true;
  };

  const tick = () => {
    tickTimer = 0;
    const now = Date.now();
    let live = false;
    countdowns.forEach((el) => {
      if (!el.isConnected) {
        countdowns.delete(el);
        visibleCountdowns.delete(el);
        try { countdownObserver?.unobserve(el); } catch (error) { /* observer not created yet */ }
        return;
      }
      if (paintCountdown(el, now) && (visibleCountdowns.has(el) || !hasIO)) live = true;
    });
    if (live && document.visibilityState === 'visible') {
      const step = reduce.matches ? 60000 - (now % 60000) : 1000 - (now % 1000);
      tickTimer = window.setTimeout(tick, step + 20);
    } else if (!live && document.visibilityState === 'visible') {
      // Calm text now: wake up once when the nearest countdown enters its last 72 h.
      const wake = Math.min(...[...countdowns]
        .map((el) => Date.parse(el.dataset.end || '') - now - URGENT_MS)
        .filter((ms) => Number.isFinite(ms) && ms > 0));
      if (Number.isFinite(wake) && wake < 2147483647) tickTimer = window.setTimeout(tick, wake + 50);
    }
  };
  const scheduleTick = () => {
    window.clearTimeout(tickTimer);
    tick();
  };
  const countdownObserver = hasIO ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) visibleCountdowns.add(entry.target);
      else visibleCountdowns.delete(entry.target);
    });
    scheduleTick();
  }) : null;

  const watchCountdowns = (scope) => {
    const nodes = scope.matches?.('[data-vzy-bf-countdown]') ? [scope] : Array.from(scope.querySelectorAll?.('[data-vzy-bf-countdown]') || []);
    let added = false;
    nodes.forEach((el) => {
      if (countdowns.has(el)) return;
      countdowns.add(el);
      countdownObserver?.observe(el);
      added = true;
    });
    if (added) scheduleTick();
  };

  /* ---------------------------------------------------------------- 5. banner ladders (cart tier) */

  const ladderIndex = new WeakMap();
  const paintLadders = (initial) => {
    document.querySelectorAll('[data-vzy-bftier-ladder]').forEach((list) => {
      const index = currentIndex(list);
      const before = ladderIndex.get(list);
      ladderIndex.set(list, index);
      // Only a real step up after the first paint (no pop on page load).
      if (!initial && before !== undefined && before > -1 && index > before && canAnimate() && inView(list)) {
        restartClass(list.children[index], 'vzy-bf-pop', 600);
      }
    });
  };

  /* ---------------------------------------------------------------- start */

  const scan = (scope) => {
    watchReveal(scope);
    watchCountdowns(scope);
    if (scope.matches?.('vizzyo-quantity-breaks[data-vzy-bf="true"]')) setupQuantityBreaks(scope);
    scope.querySelectorAll?.('vizzyo-quantity-breaks[data-vzy-bf="true"]').forEach(setupQuantityBreaks);
  };

  const start = () => {
    scan(document);
    setupDrawer();
    paintLadders(true);
    // The tier banner script repaints .is-current on vzy:bf-cart; compare after it ran.
    document.addEventListener('vzy:bf-cart', () => raf(() => paintLadders(false)));
    document.addEventListener('visibilitychange', scheduleTick);
    reduce.addEventListener?.('change', scheduleTick);
    // Product grids that load more cards (filters, pagination): pick up new tickets.
    if ('MutationObserver' in window) {
      let queued = [];
      let frame = 0;
      new MutationObserver((records) => {
        records.forEach((record) => record.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && !drawer?.contains(node)) queued.push(node);
        }));
        if (!queued.length || frame) return;
        frame = raf(() => {
          const nodes = queued;
          queued = [];
          frame = 0;
          nodes.forEach((node) => { if (node.isConnected) scan(node); });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
